import React, { memo, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDataDetailOptions, resetActiveLocation } from '@actions/data-detail-actions';
import { loadBatch, runBatchDataLoading } from '@actions/batch-actions';
import * as colors from '@constants/colors';
import { getEntityType } from '@constants/config';

import Drawer from '@components/batch/batch-upload-edit/drawer';
import Content from '@components/batch/batch-upload-edit/content';
import BatchContext from '@components/batch/batch-upload-edit/batch-context';
import LoadingDialog from '@components/batch/batch-upload-edit/loading-dialog';
import UploadBatchDialog from '@components/batch/batch-upload-edit/upload-batch-dialog';
import { getEntityTypeCategoryTypes } from '@components/config/selectors';
import TopBar from '@components/details/top-bar';
import DotmapsLoader from '@shared/dotmaps-loader';

import {
  convertCSVDataForContext,
  convertCSVErrorsForContext,
  getBatch
} from '@utils/batch-utils';

import './batch-upload-edit.scss';

const BatchUploadEdit = () => {
  const dispatch = useDispatch();
  const { id: fileId, rowId } = useParams();
  const [filter, setFilter] = useState('all');
  const [isInitialized, setIsInitialized] = useState(false);
  const batch = useMemo(() => getBatch(fileId), [fileId]);
  const batchEditState = useSelector(state => state.batchEdit[fileId]);
  const {
    map_category: categories,
    segment_impact_type: segmentImpactTypes
  } = useSelector(state => state.dataTypes);
  const entityCategoryTypes = useSelector(state => getEntityTypeCategoryTypes(state, batch?.entity));
  // The form uses the entity OPTIONS metadata, so it must be loaded for the batch entity type:
  const isOptionsLoaded = useSelector(state => Boolean(batch?.entity) && state.dataDetail.optionsDataType === batch.entity);
  // (only used once isOptionsLoaded, so they're the batch entity type ones):
  const options = useSelector(state => state.dataDetail.options);

  const isBatchLoaded = Boolean(batchEditState);

  useEffect(() => {
    // Fetch the OPTIONS once, even if the batch is already loaded, since other pages may
    // have replaced them since:
    if (batch?.entity) {
      dispatch(fetchDataDetailOptions(batch.entity)).catch(() => {
        // Without the metadata the batch isn't loaded (it's retried when returning to this page).
      });
    }
  }, [batch?.entity, dispatch]);

  useEffect(() => {
    if (isInitialized) {
      return;
    }
    if (!batch || !batch.rows || !batch.entity) {
      return;
    }
    // Wait for OPTIONS call to complete before loading the batch, since the form and the
    // entity data loading use its metadata (for proper date/field decoding):
    if (!isOptionsLoaded) {
      return;
    }
    // A batch is only loaded and processed once: if it's already in the store (i.e. when
    // returning to this page), its data loading is either still running or done.
    if (isBatchLoaded) {
      return;
    }
    setIsInitialized(true);

    const entityType = getEntityType(batch.entity);
    const entityConfig = entityType?.config;

    // Load data in the store from CSV.
    const transformedRows = batch.rows.map(row => ({
      id: row.id,
      display_row_nums: row.display_row_nums,
      data: convertCSVDataForContext(
        row, batch.entity, entityCategoryTypes, categories, segmentImpactTypes, batch.version, entityConfig?.fields
      ),
      errors: convertCSVErrorsForContext(row, batch.errors || {})
    }));

    dispatch(loadBatch({ fileId, rows: transformedRows, fileName: batch?.file?.path, entityType: batch.entity }));

    // Run coordinated loading for geocoding and entity fields
    // (it removes the batch if it can't start):
    dispatch(runBatchDataLoading(
      fileId,
      transformedRows,
      batch.entity,
      entityConfig,
      options,
      // v2 batch uploads geocode with the street_segment API first:
      batch.version === 2 ? batch.geocodingFields : null
    ));
  }, [
    batch, categories, dispatch, entityCategoryTypes, fileId, segmentImpactTypes,
    isInitialized, isOptionsLoaded, isBatchLoaded, options
  ]);

  useEffect(() => {
    // If rowId changes, reset the active location:
    dispatch(resetActiveLocation());
  }, [dispatch, rowId]);

  const rowIndex = useMemo(() => {
    return batch?.rows?.findIndex(item => item.id === rowId);
  }, [rowId, batch?.rows]);

  const batchContextValue = {
    batch,
    fileId,
    filter,
    rowId,
    rowIndex,
    setFilter
  };

  const isLoading = batchEditState?.loading ?? false;
  const loadingProgress = batchEditState?.loadingProgress ?? null;

  return (
    <BatchContext.Provider value={batchContextValue}>
      <div styleName="batch-upload-edit-container">
        <TopBar />
        <div styleName="batch-upload-edit-content">
          {isOptionsLoaded && <Drawer />}
          {isOptionsLoaded && <Content />}
          {!isOptionsLoaded && <DotmapsLoader color={colors.dotmapsGreen} />}
        </div>
        <UploadBatchDialog />
        <LoadingDialog open={isLoading} progress={loadingProgress} />
      </div>
    </BatchContext.Provider>
  );
};

export default memo(BatchUploadEdit);
